import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  AutocompleteOptions,
  Button,
  SelectFieldWrapper,
} from '@Components/index';
import Modal from '@Components/Modal';
import commonConstants from '@Constants/common.constants';
import { CallerIdType } from '@Models/callerIds.models';
import {
  useBuyPhoneNumberMutation,
  useGetAreaCodeListQuery,
  useGetAvailablePhoneNumbersMutation,
} from '@Services/userCallerIds.service';
import { BaseAlertProps } from '@Typings/global.types';
import useAlert from '@Utils/hooks/useAlert.hook';
import { useTokenTprValueHook } from '@Utils/hooks/useTokenValues.hook';

import {
  BuyVonageCallerIdModalProps,
  BuyVonageCallerIdModalInputs,
} from './BuyVonageCallerIdModal.types';

const BuyVonageCallerIdModal: FunctionComponent<BuyVonageCallerIdModalProps> = (
  props: BuyVonageCallerIdModalProps
): JSX.Element => {
  const { isOpen, setIsOpen, userId, hasUserTpr } = props;

  const { t } = useTranslation();
  const { triggerAlert } = useAlert();
  const hasTpr = useTokenTprValueHook();

  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [availableVonageNumbers, setAvailableVonageNumbers] =
    useState<number>(0);
  const [phoneNumbersArr, setPhoneNumbersArr] = useState<string[]>([]);

  // get list of all area codes
  const { data: dataOfAreaCodeList = [] } = useGetAreaCodeListQuery();

  // load available phone numbers by area code
  const [availableList, availableListMethods] =
    useGetAvailablePhoneNumbersMutation();

  // buy phone number
  const [buyPhoneNumber, buyPhoneNumberMethods] = useBuyPhoneNumberMutation();

  const areaCodes = dataOfAreaCodeList.map((code) => ({
    label: `+1 (${code.areaCode})`,
    value: code.areaCode,
  }));

  const required = t('common.formErrorMessages.required');
  const methods = useForm<BuyVonageCallerIdModalInputs>({
    resolver: yupResolver(
      yup.object({
        areaCode: yup
          .object()
          .nullable()
          .required(required)
          .shape({ value: yup.string(), label: yup.string() }),
      })
    ),
    mode: 'onChange',
    shouldUnregister: true,
  });

  /**
   * Buy Vonage phone number Form Methods: Updates the form state on submit
   */
  const onSubmitHandler: SubmitHandler<any> = () =>
    buyPhoneNumber({
      phoneAmount: '1',
      phoneArray: phoneNumbersArr,
      callerIdType: CallerIdType.REGULAR_CALLER_ID,
      userId: userId?.toString() || '',
      hasUserTpr: userId ? hasUserTpr : hasTpr,
    });

  /**
   * Error and Success alerts for buying Vonage number
   */
  useEffect(() => {
    if (buyPhoneNumberMethods.error) {
      triggerAlert(buyPhoneNumberMethods.error, setAlertState);
    }
    if (buyPhoneNumberMethods.isSuccess) {
      setIsOpen(!buyPhoneNumberMethods.data.boughtPhones);
    }
  }, [
    buyPhoneNumberMethods.isSuccess,
    buyPhoneNumberMethods.error,
    buyPhoneNumberMethods.data,
    triggerAlert,
    buyPhoneNumberMethods,
    setIsOpen,
  ]);

  const areaCodeChangeHandler = (
    _event: React.SyntheticEvent<Element, Event>,
    value: AutocompleteOptions
  ) => {
    const limit = 1;
    const selectedAreaCode = value.value;
    if (selectedAreaCode) {
      return availableList({
        areaCode: selectedAreaCode,
        limit: limit.toString(),
        userId: userId?.toString() || '',
      });
    }
  };

  useEffect(() => {
    if (availableListMethods.isSuccess) {
      if (availableListMethods.data.length > 0) {
        setAvailableVonageNumbers(availableListMethods.data.length);
        if (availableListMethods.data) {
          setPhoneNumbersArr(
            availableListMethods.data.map((item) => item.phoneNumber)
          );
        }
      } else {
        setAvailableVonageNumbers(0);
        setPhoneNumbersArr([]);
        triggerAlert(
          commonConstants.GLOBAL_MESSAGES.UNAVAILABLE_ROTATION_NUMBER,
          setAlertState,
          'error'
        );
      }
    }
  }, [availableListMethods, triggerAlert]);

  const closeModal = () => {
    setIsOpen(false);
    setAvailableVonageNumbers(0);
  };

  return (
    <>
      <Modal
        open={isOpen}
        headerText={t('userCallerIds.Add Regular CallerId')}
        onClose={() => closeModal()}
        alertState={alertState}
      >
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={methods.handleSubmit(onSubmitHandler)}
          >
            <>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                sx={{ padding: '3rem 2.4rem 2.4rem' }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mt={3.8}
                >
                  <Typography variant="h2" mr={2}>
                    {`${t('userCallerIds.Area Code')}:`}
                  </Typography>
                  <SelectFieldWrapper
                    name="areaCode"
                    options={areaCodes}
                    multiple={false}
                    textFieldProps={{
                      placeholder: t('userCallerIds.Select Area Code'),
                    }}
                    onChange={areaCodeChangeHandler}
                    disableClearable
                    sx={{
                      minWidth: '18.6rem',
                      maxWidth: '18.6rem',
                    }}
                  />
                </Box>
                <Box py={4} px={11.5}>
                  <Typography variant="main" textAlign="center">
                    {t('userCallerIds.Vonage Modal Message')}
                  </Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                sx={{
                  borderTop: '0.1rem solid #A3ABB2',
                  padding: '3rem 2.4rem 2.4rem',
                }}
              >
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    onClick={() => closeModal()}
                    color="secondary"
                    variant="outlined"
                    sx={{ ml: 'auto', mr: 1.6 }}
                  >
                    {t('common.messages.Cancel')}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={availableVonageNumbers < 1}
                  >
                    {t('common.messages.Submit')}
                  </Button>
                </Box>
              </Box>
            </>
          </Box>
        </FormProvider>
      </Modal>
    </>
  );
};

export default BuyVonageCallerIdModal;
